/**
 * Implements the internal/external comment visibility:
 *  - Populates commentVisibilityStore from Nutrient annotation/comment events
 *  - Sets default customData.isInternal = true on newly-created threads
 *  - Registers Nutrient slot factories (comment header badge + editor toggle)
 *
 * Must be called after the Nutrient viewer is ready (isReady === true).
 */

import React, {useEffect} from 'react';
import ReactDOM from 'react-dom/client';
import {flushSync} from 'react-dom';

import {commentVisibilityStore} from '../store/commentVisibilityStore';
import CommentBadgeByCommentId from '../components/CommentBadgeByCommentId';
import CommentEditorToggle from '../components/CommentEditorToggle';

// Nutrient UIFactory for commentThread.comment.header slot.
// Renders the badge only on the first comment of each thread.
const createCommentHeaderFactory = () => (instance, commentId) => {
  const container = document.createElement('div');
  container.className = 'cvb-slot-root';
  let root = null;

  return {
    render(_params) {
      if (!root) {
        root = ReactDOM.createRoot(container);
      }
      // using flushSync to synchrounously render the component and not return empty container
      flushSync(() => {
        root.render(
          React.createElement(CommentBadgeByCommentId, {commentId, instance})
        );
      });
      return container;
    },
    onUnmount(_id) {
      // Defer to avoid "unmount during render" warnings
      setTimeout(() => {
        root?.unmount();
        root = null;
      }, 0);
    },
  };
};

// Nutrient UIFactory for commentThread.footer — replaces the native Mention button.
const createEditorFactory = () => (instance, annotationId) => {
  const footerContainer = document.createElement('div');
  footerContainer.className = 'cet-slot-root';
  footerContainer.style.display = 'none';

  const toggleContainer = document.createElement('div');
  toggleContainer.className = 'cet-toggle-root';

  let root = null;
  let injected = false;
  let hiddenMentionBtn = null;

  const tryInjectIntoActionBar = () => {
    if (injected || !footerContainer.parentElement) return;

    // Skip injection for posted threads.this prevents empty cet-toggle-root divs from appearing in the action bar
    if (
      commentVisibilityStore.getSnapshot().postedAnnotationIds.has(annotationId)
    ) {
      return;
    }

    // The footer slot sits inside a wrapper: editor container.
    // find the nearest ancestor that contains the Mention button.
    const MAX_DEPTH = 6;
    let ancestor = footerContainer;
    for (let i = 0; i < MAX_DEPTH; i++) {
      if (!ancestor.parentElement) break;
      ancestor = ancestor.parentElement;
      const mentionBtn = ancestor.querySelector('button[aria-label="Mention"]');
      if (mentionBtn && mentionBtn.parentElement) {
        mentionBtn.parentElement.insertBefore(toggleContainer, mentionBtn);
        mentionBtn.style.display = 'none';
        hiddenMentionBtn = mentionBtn;
        injected = true;
        return;
      }
    }

    console.warn(
      '[useCommentVisibility] Could not find Mention button in editor action bar for annotation:',
      annotationId,
      '— the Internal/External toggle will not appear. This may indicate a Nutrient SDK DOM structure change.'
    );
  };

  return {
    render(_params) {
      if (!root) {
        root = ReactDOM.createRoot(toggleContainer);
      }
      // using flushSync to synchrounously render the component and not return empty container
      flushSync(() => {
        root.render(React.createElement(CommentEditorToggle, {annotationId}));
      });

      const isPosted = commentVisibilityStore
        .getSnapshot()
        .postedAnnotationIds.has(annotationId);

      if (!injected && !isPosted) {
        footerContainer.appendChild(toggleContainer);
        requestAnimationFrame(tryInjectIntoActionBar);
      }

      return footerContainer;
    },
    onUnmount(_id) {
      setTimeout(() => {
        root?.unmount();
        root = null;
        if (hiddenMentionBtn) {
          hiddenMentionBtn.style.display = '';
          hiddenMentionBtn = null;
        }
        if (toggleContainer.parentNode) {
          toggleContainer.parentNode.removeChild(toggleContainer);
        }
        injected = false;
      }, 0);
    },
  };
};

export const useCommentVisibility = ({instance, isReady}) => {
  useEffect(() => {
    if (!instance || !isReady) return;

    const handleAnnotationsLoad = (annotations) => {
      annotations.forEach((annotation) => {
        commentVisibilityStore.loadAnnotation(annotation, {markAsPosted: true});
      });
    };

    const handleAnnotationsCreate = (annotations) => {
      annotations.forEach((annotation) => {
        if (!annotation.isCommentThreadRoot) return;

        // New threads default to Internal.
        commentVisibilityStore.loadAnnotation(annotation, {
          defaultInternal: true,
        });
      });
    };

    const handleAnnotationsUpdate = (annotations) => {
      annotations.forEach((annotation) => {
        if (!annotation.isCommentThreadRoot) return;
        commentVisibilityStore.loadAnnotation(annotation);
      });
    };

    const handleAnnotationsDelete = (annotations) => {
      annotations.forEach((annotation) => {
        commentVisibilityStore.deleteAnnotation(annotation.id);
      });
    };

    const handleCommentsCreate = (comments) => {
      comments.forEach((comment) => {
        if (!comment.rootId) return;

        commentVisibilityStore.mapComment(comment.id, comment.rootId);
        commentVisibilityStore.markAsPosted(comment.rootId);

        // Persist isInternal when the first comment is submitted, but only if
        // the user hasn't already toggled (which would have set it already).
        const meta = commentVisibilityStore.getAnnotationMeta(comment.rootId);
        if (!meta) return;

        instance
          .getAnnotations(meta.pageIndex)
          .then((annotations) => {
            const annotation = annotations.find((a) => a.id === comment.rootId);
            if (!annotation) return;
            // Skip if isInternal is already explicitly set (user toggled the switch)
            if (annotation.customData?.isInternal != null) return;

            const currentIsInternal =
              commentVisibilityStore
                .getSnapshot()
                .visibilityMap.get(comment.rootId) ?? true;
            return instance.update(
              annotation.set('customData', {
                ...(annotation.customData ?? {}),
                isInternal: currentIsInternal,
              })
            );
          })
          .catch((err) => {
            console.warn(
              '[useCommentVisibility] Failed to persist isInternal on comment create:',
              err
            );
          });
      });
    };

    // When the first comment of a thread is deleted, promote the next comment
    const handleCommentsDelete = (deletedComments) => {
      deletedComments.forEach((comment) => {
        const {firstCommentIds} = commentVisibilityStore.getSnapshot();
        if (!firstCommentIds.has(comment.id)) return;

        const annotationId = commentVisibilityStore.getAnnotationForComment(
          comment.id
        );
        if (!annotationId) return;

        instance
          .getComments()
          .then((allComments) => {
            const nextComment = allComments.find(
              (c) => c.rootId === annotationId && c.id !== comment.id
            );
            commentVisibilityStore.promoteFirstComment(
              comment.id,
              nextComment?.id ?? null
            );
          })
          .catch((err) => {
            console.warn(
              '[useCommentVisibility] Failed to promote next comment after root deletion:',
              err
            );
            // still remove the old first comment
            commentVisibilityStore.promoteFirstComment(comment.id, null);
          });
      });
    };

    instance.addEventListener('annotations.load', handleAnnotationsLoad);
    instance.addEventListener('annotations.create', handleAnnotationsCreate);
    instance.addEventListener('annotations.update', handleAnnotationsUpdate);
    instance.addEventListener('annotations.delete', handleAnnotationsDelete);
    instance.addEventListener('comments.create', handleCommentsCreate);
    instance.addEventListener('comments.delete', handleCommentsDelete);

    // Eagerly fetch all annotations and comments on startup. Per-page
    // annotations.load events may fire before this listener is registered,
    // and getComments() populates the comment→annotation map for existing threads.
    let cancelled = false;
    (async () => {
      const pageCount = instance.totalPageCount;
      for (let i = 0; i < pageCount; i++) {
        if (cancelled) return;
        try {
          const pageAnnotations = await instance.getAnnotations(i);
          if (cancelled) return;
          pageAnnotations.forEach((annotation) => {
            commentVisibilityStore.loadAnnotation(annotation, {
              markAsPosted: true,
            });

            // Backfill: for existing comment threads, set isInternal to true
            if (
              annotation.isCommentThreadRoot &&
              annotation.customData?.isInternal == null
            ) {
              instance
                .update(
                  annotation.set('customData', {
                    ...(annotation.customData ?? {}),
                    isInternal: true,
                  })
                )
                .catch((err) => {
                  console.warn(
                    '[useCommentVisibility] Failed to backfill isInternal:',
                    annotation.id,
                    err
                  );
                });
            }
          });
        } catch {
          // page may not be loadable yet
        }
      }

      if (cancelled) return;
      try {
        const allComments = await instance.getComments();
        if (cancelled) return;

        const sortedComments = [...allComments].sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        );
        sortedComments.forEach((comment) => {
          if (comment.id && comment.rootId) {
            commentVisibilityStore.mapComment(comment.id, comment.rootId);
          }
        });
      } catch {
        // getComments may fail if the instance is already unloaded
      }
    })();

    instance.setUI({
      commentThread: {
        comment: {
          header: createCommentHeaderFactory(),
        },
        footer: createEditorFactory(),
      },
    });

    // cleanup
    return () => {
      cancelled = true;
      try {
        instance.removeEventListener('annotations.load', handleAnnotationsLoad);
        instance.removeEventListener(
          'annotations.create',
          handleAnnotationsCreate
        );
        instance.removeEventListener(
          'annotations.update',
          handleAnnotationsUpdate
        );
        instance.removeEventListener(
          'annotations.delete',
          handleAnnotationsDelete
        );
        instance.removeEventListener('comments.create', handleCommentsCreate);
        instance.removeEventListener('comments.delete', handleCommentsDelete);
      } catch {
        // instance may already be unloaded
      }
      commentVisibilityStore.reset();
    };
  }, [instance, isReady]);
};
